import { h } from '@stencil/core';
import type { ObservableMap } from '@stencil/store';

import { HomeAwayTeam, PlayerEventSubType } from '../../../../../services/interfaces';
import type { MatchHeaderStoreState } from '../../../../../services/interfaces/match-header';
import { isSuspendedMatch } from '../../../match-unit/helpers';
import {
  getDisplayedPenaltiesCount,
  isFinishedMatch,
  isLiveMatchPenalties,
  matchPenaltyScore,
  matchPenaltyScorers,
} from '../../../match-unit/helpers/match-unit.helpers';

type Props = {
  state: ObservableMap<MatchHeaderStoreState>['state'];
};

const MatchPenaltyScore = ({ state }: Props) => {
  const match = state.matchData;
  if (!match) return null;

  const isLivePenalties = isLiveMatchPenalties(match.status, match.phase);
  const suspendedMatch = isSuspendedMatch(match.status);
  const finishedMatch = isFinishedMatch(match.status);
  const homeTeamPenalties = matchPenaltyScore(match, HomeAwayTeam.HOME);
  const awayTeamPenalties = matchPenaltyScore(match, HomeAwayTeam.AWAY);

  const havePenaltiesScore = isLivePenalties || (finishedMatch && (!!homeTeamPenalties || !!awayTeamPenalties));

  if (!havePenaltiesScore) return null;

  const print = (penaltyScore: number) => {
    const penalty = penaltyScore ?? 0;
    if (!isLivePenalties) return `(${penalty})`;

    return penalty;
  };

  return (
    <div
      class={{
        'pk-match-header__penalties--score': true,
        live: isLivePenalties,
        suspended: suspendedMatch,
      }}
      part="penalties"
    >
      <span class="home" part="penalties-home">
        {print(homeTeamPenalties)}
      </span>
      {' - '}
      <span class="away" part="penalties-away">
        {print(awayTeamPenalties)}
      </span>
    </div>
  );
};

const MatchLivePenalties = ({ state }: Props): HTMLDivElement => {
  const match = state.matchData;
  if (!match) return null;

  const isLivePenalties: boolean = isLiveMatchPenalties(match.status, match.phase);

  if (!isLivePenalties) return null;

  const homeTeamPenalties = matchPenaltyScorers(match, HomeAwayTeam.HOME) ?? [];
  const awayTeamPenalties = matchPenaltyScorers(match, HomeAwayTeam.AWAY) ?? [];
  const penaltySlotCount = getDisplayedPenaltiesCount(homeTeamPenalties, awayTeamPenalties);

  /**
   * Renders penalty item for a team
   * Includes symbol for scored and missed
   * For penalty not scored/missed, displays an empty marker
   * @param penaltyType
   * @param penaltyNumber
   * @param type
   * @returns HTMLElement
   */
  const renderPenaltyItem = (
    penaltyType: PlayerEventSubType,
    penaltyNumber: number,
    type: HomeAwayTeam,
  ): HTMLElement => {
    const isGoal = ([PlayerEventSubType.GOAL, PlayerEventSubType.SCORED] as PlayerEventSubType[]).includes(penaltyType);
    const isMissed = ([PlayerEventSubType.MISS, PlayerEventSubType.MISSED] as PlayerEventSubType[]).includes(
      penaltyType,
    );

    return (
      <span
        key={`${type}-penalty-${penaltyNumber}`}
        class={{
          penalty: true,
          'no-border': isGoal || isMissed,
        }}
        part={`penalty-${type}-number`}
      >
        {isGoal && <pk-icon size="8" name="football-penalty-goal" title="penalty goal" />}
        {isMissed && <pk-icon size="8" name="football-penalty-out" title="penalty out" />}
      </span>
    );
  };

  /**
   * Renders penalties for a team (home or away)
   * @param {HomeAwayTeam} type
   * @returns {HTMLElement[]}
   */
  const renderPenalties = (type: HomeAwayTeam): HTMLElement[] => {
    if (!penaltySlotCount) return;

    const teamPenalties = type === HomeAwayTeam.HOME ? homeTeamPenalties : awayTeamPenalties;

    return [...Array(penaltySlotCount)].map((_p: number, i: number) => {
      const penaltyType = teamPenalties?.[i]?.penaltyType;
      const penaltyNumber = i + 1;

      return renderPenaltyItem(penaltyType, penaltyNumber, type);
    });
  };

  const isCompactMode = state.isCompactMode;
  const hideEmptyPenaltyItems = !homeTeamPenalties?.length && !awayTeamPenalties?.length && isCompactMode;

  return (
    <div class={{ 'pk-match-header__penalties': true }} part="penalties-wrapper">
      <div
        class={{ 'pk-match-header__penalties--home': !hideEmptyPenaltyItems, hidden: hideEmptyPenaltyItems }}
        part="penalties-home-wrapper"
      >
        {renderPenalties(HomeAwayTeam.HOME)}
      </div>

      <MatchPenaltyScore state={state} />

      <div
        class={{ 'pk-match-header__penalties--away': !hideEmptyPenaltyItems, hidden: hideEmptyPenaltyItems }}
        part="penalties-away-wrapper"
      >
        {renderPenalties(HomeAwayTeam.AWAY)}
      </div>
    </div>
  );
};

export { MatchLivePenalties, MatchPenaltyScore };
